/**
 * Redux Actions for Shift Management
 * @description Contains asynchronous actions for fetching, creating, updating, and deleting shifts in Firestore, as well as starting shifts.
 *
 * @actions
 *   @function fetchShifts - Fetches all shifts for a specific venue for the selected week and dispatches success or failure actions.
 *   @function fetchEmployeeShifts - Fetches shifts for a specific employee based on venue and employee IDs, dispatching relevant actions.
 *   @function startShift - Calls a Firebase Function to start a shift and logs the result or error.
 *   @function createNewShift - Creates a new shift in Firestore and dispatches actions to update the state.
 *   @function updateExistingShift - Updates an existing shift in Firestore and dispatches actions to reflect the changes.
 *   @function deleteExistingShift - Deletes a shift from Firestore and dispatches actions to remove it from the state.
 *
 * @firebase
 *   - Uses Firestore methods: `getDocs`, `addDoc`, `updateDoc`, `deleteDoc` for CRUD operations on shifts.
 *   - Uses Firebase Functions for server-side logic, such as starting a shift.
 *
 * @utilityFunctions
 *   @function convertTimestampsToUnix - Converts Firestore Timestamp objects to Unix time (in milliseconds) for easy handling in JavaScript.
 *
 * @example
 * import { fetchShifts, createNewShift, updateExistingShift, deleteExistingShift } from './userActions';
 */

import {
  fetchShiftsStart,
  fetchShiftsSuccess,
  fetchShiftsFailure,
  createShiftStart,
  createShiftSuccess,
  createShiftFailure,
  updateShiftStart,
  updateShiftSuccess,
  updateShiftFailure,
  updateShift,
  deleteShiftStart,
  deleteShiftSuccess,
  deleteShiftFailure,
  fetchShiftsByEmployeeSuccess,
  startShiftStart,
  startShiftSuccess,
  startShiftFailure,
  endShiftStart,
  endShiftSuccess,
  endShiftFailure,
  updateShiftBreaks,
  fetchAvailabilitySuccess,
} from '../slices/shiftSlice';
import {
  fetchFutureShiftsStart,
  fetchFutureShiftsSuccess,
  fetchFutureShiftsFailure,
} from '../slices/futureShiftsSlice';
import { db } from '../../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { convertTimestampsToUnix } from '../../utils/dateUtils';
import { validateShiftAll } from '../shifts/shiftsService';
import dayjs from 'dayjs';

export const fetchShifts = () => async (dispatch, getState) => {
  try {
    dispatch(fetchShiftsStart());

    const { id: vid, startOfWeekIndex } = getState().currentUser.venue;

    if (!vid) {
      throw new Error('No id found in state.');
    }

    let { selectedWeek } = getState().shifts;

    if (!selectedWeek || !selectedWeek.start || !selectedWeek.end) {
      const today = dayjs();
      const startOfWeek = today.startOf('week').add(startOfWeekIndex, 'day');
      const endOfWeek = startOfWeek.add(6, 'day');

      selectedWeek = {
        start: startOfWeek.toISOString(),
        end: endOfWeek.toISOString(),
      };
    }

    const startTimestamp = Timestamp.fromDate(new Date(selectedWeek.start));
    const endTimestamp = Timestamp.fromDate(new Date(selectedWeek.end));

    // Query Firestore
    const q = query(
      collection(db, 'shifts'),
      where('vid', '==', vid),
      where('start', '>=', startTimestamp),
      where('start', '<=', endTimestamp)
    );

    const querySnapshot = await getDocs(q);

    // Fetch shifts and their breaks
    const shiftsData = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const shift = {
          id: doc.id,
          ...convertTimestampsToUnix(doc.data()),
        };

        // Fetch breaks for this shift
        const breaksRef = collection(db, `shifts/${shift.id}/breaks`);
        const breaksSnapshot = await getDocs(breaksRef);
        const breaks = breaksSnapshot.docs.map((breakDoc) => ({
          id: breakDoc.id,
          ...convertTimestampsToUnix(breakDoc.data()),
        }));

        // Return shift with breaks
        return { ...shift, breaks };
      })
    );

    // Dispatch success action
    dispatch(fetchShiftsSuccess(shiftsData));
  } catch (error) {
    console.error('Failed to fetch shifts:', error);
    dispatch(fetchShiftsFailure(error.message));
  }
};

export const fetchBreaksAndUpdateShift = (shiftId, isNewShift) => async (dispatch) => {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  try {
    if (isNewShift) {
      await sleep(800);
    }

    const breaksRef = collection(db, `shifts/${shiftId}/breaks`);
    const breaksSnapshot = await getDocs(breaksRef);

    const breaks = breaksSnapshot.docs.map((breakDoc) => ({
      id: breakDoc.id,
      ...convertTimestampsToUnix(breakDoc.data()),
    }));

    dispatch(updateShiftBreaks({ shiftId, breaks }));
    return breaks;
  } catch (error) {
    console.error(`Failed to fetch breaks for shift ${shiftId}:`, error);
  }
};

// Action to fetch shifts for a specific employee from Firestore
export const fetchEmployeeShifts = (eid) => async (dispatch, getState) => {
  try {
    dispatch(fetchShiftsStart());

    const { id: vid, startOfWeekIndex } = getState().currentUser.venue;

    if (!vid) {
      throw new Error('Venue ID not found in state');
    }

    // Query Firestore to fetch shifts for a specific employee
    const q = query(collection(db, 'shifts'), where('vid', '==', vid), where('eid', '==', eid));
    const querySnapshot = await getDocs(q);

    const employeeShifts = [];
    querySnapshot.forEach((doc) => {
      const shiftData = doc.data();
      employeeShifts.push({
        id: doc.id,
        ...convertTimestampsToUnix(shiftData), // Convert timestamp fields to serializable data
      });
    });
    dispatch(fetchShiftsSuccess(employeeShifts));
    dispatch(
      fetchShiftsByEmployeeSuccess({
        shifts: employeeShifts,
        startOfWeekIndex,
      })
    );
  } catch (error) {
    dispatch(fetchShiftsFailure(error.message));
  }
};

// Fetch all future shifts for employees at a venue
export const fetchFutureShifts = () => async (dispatch, getState) => {
  try {
    dispatch(fetchFutureShiftsStart());

    const { id: vid } = getState().currentUser.venue;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const q = query(collection(db, 'shifts'), where('vid', '==', vid), where('start', '>=', today));

    const querySnapshot = await getDocs(q);
    const futureShifts = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampsToUnix(doc.data()),
    }));

    dispatch(fetchFutureShiftsSuccess(futureShifts));
  } catch (error) {
    console.error('Failed to fetch future shifts:', error);
    dispatch(fetchFutureShiftsFailure(error.message));
  }
};

// Action to start a shift in Firestore
export const startShift = (shiftId) => async (dispatch) => {
  try {
    dispatch(startShiftStart());

    const functions = getFunctions();
    const startShiftCallable = httpsCallable(functions, 'startShift');

    const result = await startShiftCallable({ sid: shiftId });

    if (result) {
      dispatch(startShiftSuccess({ id: shiftId, status: 'in_progress' }));
      dispatch(fetchShifts());
    }
  } catch (error) {
    console.error('Error starting shift:', error);
    dispatch(startShiftFailure(error.message));
  }
};

// Action to end a shift in Firestore
export const endShift = (shiftId) => async (dispatch) => {
  try {
    dispatch(endShiftStart());

    const functions = getFunctions();
    const endShiftCallable = httpsCallable(functions, 'endShift');

    const result = await endShiftCallable({ sid: shiftId });

    if (result) {
      console.log('Shift ended successfully');
      dispatch(endShiftSuccess());
    }
  } catch (error) {
    console.error('Error ending shift:', error);
    dispatch(endShiftFailure(error.message));
  }
};

// Action to create a new shift in Firestore
export const createShift = (shiftData) => async (dispatch, getState) => {
  try {
    dispatch(createShiftStart());

    const { id: vid } = getState().currentUser.venue;

    if (!vid) {
      throw new Error('Venue ID not found in storage');
    }

    // Build the object for Firebase
    const shiftObjectForFirebase = {
      vid,
      assignedName: null,
      assignedProfileImageUrl: null,
      clockedInAt: null,
      clockedOutAt: null,
      createdAt: new Date(shiftData.createdAt),
      start: new Date(shiftData.start),
      end: new Date(shiftData.end),
      name: shiftData.type.name,
      notes: shiftData.notes || '',
      scheduled: true,
      projectedCost: shiftData.projectedCost || 0,
      eid: null,
    };

    const docRef = await addDoc(collection(db, 'shifts'), shiftObjectForFirebase);
    const newShiftFirebase = { id: docRef.id, ...shiftObjectForFirebase };

    // Convert to Redux format, Serializing dates
    const newShiftRedux = {
      ...newShiftFirebase,
      createdAt: shiftData.createdAt.getTime(),
      start: shiftData.start.getTime(),
      end: shiftData.end.getTime(),
    };

    dispatch(createShiftSuccess(newShiftRedux));
    dispatch(fetchShifts()); // Update the shift list
    return newShiftFirebase;
  } catch (error) {
    console.error('Failed to create shift:', error);
    dispatch(createShiftFailure(error.message));
  }
};

export const updateExistingShift = (id, updatedFields, startOfWeekIndex) => async (dispatch, getState) => {
  try {
    dispatch(updateShiftStart());
    const shiftRef = doc(db, 'shifts', id);
    await updateDoc(shiftRef, updatedFields);
    const state = getState();
    const existingShift = state.shifts.shifts.find((shift) => shift.id === id);

    if (!existingShift) {
      throw new Error('Shift not found in Redux state');
    }

    // Transform only the fields relevant to Redux
    const updatedShiftRedux = {
      ...existingShift,
      ...updatedFields,
      ...(updatedFields.createdAt && {
        createdAt:
          updatedFields.createdAt instanceof Date ? updatedFields.createdAt.getTime() : updatedFields.createdAt,
      }),
      ...(updatedFields.start && {
        start: updatedFields.start instanceof Date ? updatedFields.start.getTime() : updatedFields.start,
      }),
      ...(updatedFields.end && {
        end: updatedFields.end instanceof Date ? updatedFields.end.getTime() : updatedFields.end,
      }),
    };

    // Update state in Redux
    dispatch(updateShift({ id, updatedShiftRedux, startOfWeekIndex }));
    dispatch(updateShiftSuccess());
    // Keep the UI up to date
    dispatch(fetchShifts());
  } catch (error) {
    console.error('Failed to update shift:', error);
    dispatch(updateShiftFailure(error.message));
  }
};

// Action to delete an existing shift in Firestore
export const deleteExistingShift = (id) => async (dispatch) => {
  try {
    dispatch(deleteShiftStart());
    await deleteDoc(doc(db, 'shifts', id));
    dispatch(deleteShiftSuccess(id));
    // Fetch the latest shifts to keep the UI updated
    dispatch(fetchShifts());
  } catch (error) {
    console.error('Error deleting shift:', error);
    dispatch(deleteShiftFailure(error.message));
  }
};

export const fetchAvailability = (employeeList, shiftInfo, venueId) => async (dispatch) => {
  const result = await validateShiftAll(shiftInfo.start, shiftInfo.end, employeeList.map(_ => _.id), venueId)
  dispatch(fetchAvailabilitySuccess(result));
};
